<template>

  <view :class="autoClass">


    <image
      v-tap="handleClick"
      class="c-img"
      lazy-load
      :mode="mode"
      :style="style"
      :src="srcUrl"
      :show-menu-by-longpress="showMenuByLongpress"
      @error="handleError"
      @load="handleLoad"
      @click="handleClick"
    >
    </image>
















    <!-- 图片加载中占位 -->
    <view v-if="showLoad && !imgLoadedRef" class="load-skeleton__wrap" :style="skeletonWrapStyle">
      <view class="load-skeleton">
        <image
          class="load-skeleton__icon"
          :style="skeletonIconStyle"
          src="/static/common/load-img.png"
        />
      </view>
    </view>


    <template v-for="(item, index) in hotZoneListRef" :key="index">
      <view
        v-tap.stop="() => handleHotZoneClick(item)"
        class="hot-zone"
        :style="getHotZoneStyle(item)"
        @click.stop="handleHotZoneClick(item)"
      ></view>
    </template>











  </view>

</template>

<script setup lang="ts">
import { CLICK_EVENT, CLICK_HOT_ZONE_EVENT } from '@/constants';
import { commonUtils } from '@/utils';
import errorManager, { ErrorManagerEntryType } from '@/utils/errorManager';
import { computed, nextTick, ref, toRefs, useAttrs, watch } from 'vue';

interface Props {
  src?: string; // 图片路径
  showLoad?: boolean; // 图片显示加载效果
  imgMode?: string; // 图片渲染模式
  remoteName?: string; // 远程仓库的图片名
  size?: number | number[]; // 宽高
  margin?: number | number[]; // 外边距
  borderColor?: string; // 边框颜色
  radius?: number | number[]; // 圆角
  specifyHeight?: number; // 指定高度，则宽度自适应即100%
  hotZone?: boolean; // 是否计算绘制热区；注：父级样式需设置position: relative；
  hotZoneObject?: AnyObject | null; // 热区对象数据
  showMenuByLongpress?: boolean;
  // 节点骨架参数
  skeletonOptions?: {
    iconStyle?: string;
    wrapStyle?: string;
    shadow?: boolean;
    iconSize?: 'large' | 'medium' | 'small';
  };
}

const props = withDefaults(defineProps<Props>(), {
  src: '',
  showLoad: false,
  imgMode: 'aspectFill',
  hotZone: false,
  hotZoneObject: null,
  showMenuByLongpress: false,
});
const {
  src,
  showLoad,
  imgMode,
  remoteName,
  size,
  margin,
  radius,
  borderColor,
  specifyHeight,
  hotZone,
  hotZoneObject,
  skeletonOptions,
  showMenuByLongpress,
} = toRefs(props);


const attrs = useAttrs();
const autoClass = computed(() => {
  if (attrs.class) {
    return attrs.class;
  } else if (size.value) {
    return;
  } else {
    return 'h5-img-box';
  }
});


const emits = defineEmits([CLICK_EVENT, CLICK_HOT_ZONE_EVENT]);

const hotZoneListRef = ref<AnyObject[]>([]);
const imgLoadedRef = ref(false);
const imgOpacityRef = ref(false);

const srcUrl = computed(() => {
  if (remoteName?.value) {
    return commonUtils.img.remote(remoteName?.value);
  }
  return src.value;
});

/** 图片缩放模式 */
const mode = computed(() => {
  // 如果是热区图片，则不保持纵横比缩放图片，使图片的宽高完全拉伸至填满 image 元素
  // 所以给予的图片宽高比例需严格按照设计图比例
  // 尽可能1:1还原热区在图片中的比例
  // if (hotZone.value) {
  //   return 'scaleToFill';
  // }
  return imgMode.value;
});

/** 图片样式 */
const style = computed(() => {
  let _style: AnyObject = {};

  const sizeValue = size?.value;
  const radiusValue = radius?.value;

  if (Array.isArray(radiusValue)) {
    _style = {
      ..._style,
      ...commonUtils.style.formatSpacing(radiusValue, 'border-radius'),
    };
  } else if (radiusValue) {
    _style = {
      ..._style,
      'border-radius': `${radius.value}rpx`,
    };
  }

  if (borderColor?.value) {
    _style = {
      ..._style,
      border: `2rpx solid ${borderColor.value}`,
    };
  }

  const marginValue = margin?.value;
  if (typeof marginValue === 'number') {
    _style = {
      ..._style,
      margin: `${marginValue}rpx`,
    };
  } else if (Array.isArray(marginValue)) {
    _style = {
      ..._style,
      margin: `${marginValue[0]}rpx ${marginValue[1]}rpx ${marginValue[2]}rpx ${marginValue[3]}rpx`,
    };
  }

  if (Array.isArray(sizeValue)) {
    _style = {
      ..._style,
      width: `${sizeValue[0]}rpx`,
      height: `${sizeValue[1]}rpx`,
    };
  } else if (specifyHeight?.value) {
    _style = {
      ..._style,
      width: `100%`,
      height: `${specifyHeight.value}rpx`,
    };
  } else if (sizeValue) {
    _style = {
      ..._style,
      width: `${sizeValue}rpx`,
      height: `${sizeValue}rpx`,
    };
  }

  if (showLoad.value) {
    if (imgOpacityRef.value) {
      _style = {
        ..._style,
        opacity: 1,
        transition: `opacity 100ms linear`,
      };
    } else {
      _style = {
        ..._style,
        opacity: 0,
      };
    }
  }

  return _style;
});

const skeletonIconStyle = computed(() => {
  const sv = skeletonOptions.value;
  let style = sv?.iconStyle ?? {};
  if (sv?.iconSize === 'medium') {
    style = {
      ...style,
      width: `90rpx`,
      height: `90rpx`,
    };
  } else if (sv?.iconSize === 'small') {
    style = {
      ...style,
      width: `60rpx`,
      height: `60rpx`,
    };
  }
  return style;
});

const skeletonWrapStyle = computed(() => {
  let style = skeletonOptions.value?.wrapStyle ?? {};

  if (skeletonOptions.value?.shadow) {
    style = {
      ...style,
      'box-shadow': '0 0 16rpx #f6dadd',
      'border-radius': '10rpx',
    };
  }

  return style;
});

const handleClick = () => emits(CLICK_EVENT);

const handleError = (err: Event) => {
  console.log(`图片加载错误:`, err);
  errorManager.report(ErrorManagerEntryType.resourceLoad, {
    message: err?.detail?.errMsg ?? '图片加载错误',
  });
};

const handleLoad = (e: AnyObject) => {
  if (showLoad.value && e.detail.width) {
    nextTick(() => {
      // setTimeout(() => {
      imgLoadedRef.value = true;
      setTimeout(() => {
        imgOpacityRef.value = true;
      }, 100);
      // }, 3000);
    });
  }
};

const handleHotZoneClick = (item: AnyObj) => {
  if (item._placeholder) return;
  emits(CLICK_HOT_ZONE_EVENT, item);
  // businessUtils.mp.customJump(item._source.jumpValue, item._source.jumpType);
};

// const handleLongPress = () => {
//   if (showMenuByLongpress.value) {
//     // 调用系统预览图片API
//     uni.previewImage({
//       urls: [srcUrl.value],
//       current: srcUrl.value,
//     });
//   }
// };

const getHotZoneStyle = (item: AnyObject) => {
  return {
    width: item.xRatio,
    height: item.yRatio,
    top: item.tRatio,
    left: item.lRatio,
  };
};

watch(
  () => [hotZone.value, hotZoneObject.value],
  (news) => {
    if (news[0]) {
      const hv = news[1] as AnyObject;
      if (hv) {
        const hotZones = commonUtils.img.getHotZone(hv.jump, {
          width: hv.imgWidth,
          height: hv.imgHeight,
        });
        
        // 如果启用了热区但没有热区数据，添加一个占位热区
        if (hotZones.length === 0 && hotZone.value) {
          hotZoneListRef.value = [{
            _source: { ...hv },
            xRatio: '100%',
            yRatio: '100%',
            lRatio: '0%',
            tRatio: '0%',
            _placeholder: true // 标记这是一个占位热区
          }];
        } else {
          hotZoneListRef.value = hotZones;
        }
      }
    } else {
      hotZoneListRef.value = [];
    }
  },
  { immediate: true }
);
</script>

<style scoped lang="scss">
.h5-img-box {
  width: 100%;
  height: 100%;
}

.c-img {
  position: relative;
  display: block;
  width: 100%;
  height: 100%;
  will-change: transform;
}

.load-skeleton__wrap {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 9;
  width: 100%;
  height: 100%;
}

.load-skeleton {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  width: 100%;
  height: 100%;
  border-radius: 10rpx;
  // background: linear-gradient(90deg, #fff6f5 0%, #ffe5dd 50%, #fff6f5 100%);
  background-color: $skeleton_bg;
  font-size: 24rpx;
  color: $N7;
}

.load-skeleton__icon {
  width: 120rpx;
  height: 120rpx;
}

.hot-zone {
  position: absolute;
  z-index: 90;
  // background: rgba(0, 0, 0, 0.5);
}
</style>
